import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  useTheme,
} from "@mui/material";
import {
  VideoCall as VideoCallIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Description as DocumentIcon,
  Launch as LaunchIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AssignmentTurnedIn as CompletedIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  getMeetings,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  addMeetingParticipants,
  removeMeetingParticipant,
  saveMeetingDocument,
} from "services/company/meeting/meetingService";
import { getEmployees } from "services/company/employee/employeeService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

import CreateEditMeetingModal from "./components/CreateEditMeetingModal";
import DocumentEditorViewerModal from "./components/DocumentEditorViewerModal";
import AddParticipantsModal from "./components/AddParticipantsModal";
import MeetingDetailsModal from "./components/MeetingDetailsModal";

export const parseMeetingDate = (datetimeStr) => {
  if (!datetimeStr) return null;
  if (datetimeStr instanceof Date) return datetimeStr;
  const str = String(datetimeStr).trim();
  const isoStr = str.includes(" ") ? str.replace(" ", "T") : str;
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
};

export default function Meetings() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters & display
  const [search, setSearch] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  // Modals state
  const [isCreateEditOpen, setIsCreateEditOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);

  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentMeeting, setDocumentMeeting] = useState(null);

  const [isAddPeopleOpen, setIsAddPeopleOpen] = useState(false);
  const [addPeopleMeeting, setAddPeopleMeeting] = useState(null);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsMeeting, setDetailsMeeting] = useState(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Fetch meetings and employees
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterTab === "my_meetings") {
        params.my_meetings = "true";
      } else if (filterTab !== "all") {
        params.filter = filterTab;
      }

      const [meetingsRes, employeesRes] = await Promise.all([
        getMeetings(params),
        getEmployees().catch(() => ({ data: [] })),
      ]);

      const meetingList = meetingsRes?.data || [];
      setMeetings(meetingList);

      const empList = employeesRes?.data || employeesRes?.response || [];
      setEmployees(empList);
    } catch (err) {
      console.error("Failed to load meetings data:", err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to load meetings",
      );
    } finally {
      setLoading(false);
    }
  }, [filterTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Client-side search filtering
  const filteredMeetings = useMemo(() => {
    if (!search.trim()) return meetings;
    const term = search.toLowerCase();
    return meetings.filter((m) => {
      const matchTitle = (m.title || "").toLowerCase().includes(term);
      const matchCreator = (m.creator_name || "").toLowerCase().includes(term);
      const matchDoc = (m.document_title || "").toLowerCase().includes(term);
      const matchParticipant = (m.participants || []).some((p) =>
        (p.employee_name || p.email || "").toLowerCase().includes(term),
      );
      return matchTitle || matchCreator || matchDoc || matchParticipant;
    });
  }, [meetings, search]);

  // Stats
  const stats = useMemo(() => {
    const total = meetings.length;
    const now = new Date();
    const todayStr = now.toDateString();

    let upcoming = 0;
    let today = 0;
    let withDocs = 0;

    meetings.forEach((m) => {
      const d = parseMeetingDate(m.meeting_datetime);
      if (d) {
        if (d >= now) upcoming += 1;
        if (d.toDateString() === todayStr) today += 1;
      }
      if (m.document_content && m.document_content.trim()) withDocs += 1;
    });

    return { total, upcoming, today, withDocs };
  }, [meetings]);

  const handleCopyLink = (m) => {
    if (!m.meeting_link) return;
    navigator.clipboard.writeText(m.meeting_link);
    setCopiedLinkId(m.id);
    setTimeout(() => setCopiedLinkId(null), 2000);
    showSuccessToast("Meeting link copied to clipboard!");
  };

  const handleSaveMeeting = async (payload) => {
    try {
      setActionLoading(true);
      if (editingMeeting && editingMeeting.id) {
        await updateMeeting(editingMeeting.id, payload);
        showSuccessToast("Meeting updated successfully!");
      } else {
        await createMeeting(payload);
        showSuccessToast("Meeting scheduled successfully!");
      }
      setIsCreateEditOpen(false);
      setEditingMeeting(null);
      await loadData();
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to save meeting",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveDocument = async (payload) => {
    try {
      setActionLoading(true);
      await saveMeetingDocument(payload);
      showSuccessToast("Meeting document prepared and saved!");
      await loadData();
      if (detailsMeeting && detailsMeeting.id === payload.meeting_id) {
        setDetailsMeeting((prev) => ({
          ...prev,
          document_id: payload.document_id,
          document_title: payload.title,
          document_content: payload.content,
          document_updated_at: new Date().toISOString(),
        }));
      }
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to save document",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddParticipants = async (meetingId, employeeIds) => {
    try {
      setActionLoading(true);
      await addMeetingParticipants(meetingId, employeeIds);
      showSuccessToast("Attendees added to meeting!");
      setIsAddPeopleOpen(false);
      setAddPeopleMeeting(null);
      await loadData();
      if (detailsMeeting && detailsMeeting.id === meetingId) {
        const updated = await getMeetings();
        const found = (updated?.data || []).find((m) => m.id === meetingId);
        if (found) setDetailsMeeting(found);
      }
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to add attendees",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveParticipant = async (meetingId, employeeId) => {
    try {
      await removeMeetingParticipant(meetingId, employeeId);
      showSuccessToast("Attendee removed");
      await loadData();
      if (detailsMeeting && detailsMeeting.id === meetingId) {
        setDetailsMeeting((prev) => ({
          ...prev,
          participants: (prev.participants || []).filter(
            (p) => p.employee_id !== employeeId,
          ),
        }));
      }
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to remove attendee",
      );
    }
  };

  const handleDeleteMeeting = async () => {
    if (!deleteConfirmId) return;
    try {
      setActionLoading(true);
      await deleteMeeting(deleteConfirmId);
      showSuccessToast("Meeting deleted successfully");
      setDeleteConfirmId(null);
      setIsDetailsOpen(false);
      setDetailsMeeting(null);
      await loadData();
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to delete meeting",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
        // backgroundColor: isDark ? "#0b1437" : "#f4f7fe",
      }}
    >
      {/* ================= HEADER SECTION ================= */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.25) 100%)",
              color: "#d97706",
            }}
          >
            <VideoCallIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: isDark ? "#ffffff" : "#1b2559",
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", sm: "1.875rem" },
              }}
            >
              Meetings
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#a3aed0" : "#707eae",
                mt: 0.25,
              }}
            >
              Schedule video calls, manage attendees, and prepare meeting
              minutes & agendas.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton
              onClick={loadData}
              disabled={loading}
              size="medium"
              sx={{
                bgcolor: isDark ? "#111c44" : "#ffffff",
                border: "1px solid",
                borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                borderRadius: "12px",
                color: isDark ? "#a3aed0" : "#707eae",
                "&:hover": {
                  bgcolor: isDark ? "#1b2559" : "#f8fafc",
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingMeeting(null);
              setIsCreateEditOpen(true);
            }}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "12px",
              px: 3,
              py: 1.1,
              fontWeight: 700,
              fontSize: "0.875rem",
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                boxShadow: "0 6px 20px rgba(245, 158, 11, 0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Schedule Meeting
          </Button>
        </Stack>
      </Box>

      {/* ================= STATS ROW ================= */}
      {/* <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2.5,
          mb: 3.5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
              color: "#3b82f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: isDark ? "#a3aed0" : "#a0aec0",
                textTransform: "uppercase",
              }}
            >
              Total Meetings
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: isDark ? "#ffffff" : "#2b3674",
              }}
            >
              {stats.total}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
              color: "#10b981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ScheduleIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: isDark ? "#a3aed0" : "#a0aec0",
                textTransform: "uppercase",
              }}
            >
              Upcoming
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: isDark ? "#ffffff" : "#2b3674",
              }}
            >
              {stats.upcoming}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7",
              color: "#f59e0b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TimeIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: isDark ? "#a3aed0" : "#a0aec0",
                textTransform: "uppercase",
              }}
            >
              Today's Calls
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: isDark ? "#ffffff" : "#2b3674",
              }}
            >
              {stats.today}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(139, 92, 246, 0.15)" : "#f5f3ff",
              color: "#8b5cf6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DocumentIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: isDark ? "#a3aed0" : "#a0aec0",
                textTransform: "uppercase",
              }}
            >
              MOM / Docs Prepared
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: isDark ? "#ffffff" : "#2b3674",
              }}
            >
              {stats.withDocs}
            </Typography>
          </Box>
        </Paper>
      </Box> */}

      {/* ================= CONTROLS & FILTER BAR ================= */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3.5,
          borderRadius: "16px",
          bgcolor: isDark ? "#111c44" : "#ffffff",
          border: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
        }}
      >
        {/* TABS */}
        <Tabs
          value={filterTab}
          onChange={(e, val) => setFilterTab(val)}
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": {
              backgroundColor: "#f59e0b",
              height: 3,
              borderRadius: "3px",
            },
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 700,
              color: isDark ? "#a3aed0" : "#707eae",
              "&.Mui-selected": {
                color: isDark ? "#ffffff" : "#2b3674",
              },
            },
          }}
        >
          <Tab value="all" label="All Hierarchy Meetings" />
          <Tab value="my_meetings" label="My Meetings" />
          <Tab value="upcoming" label="Upcoming" />
          <Tab value="today" label="Today" />
          <Tab value="past" label="Past" />
        </Tabs>

        {/* SEARCH & DISPLAY TOGGLES */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder="Search meetings, notes, attendees..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: isDark ? "#a3aed0" : "#a0aec0" }}
                  />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "12px",
                fontSize: "0.875rem",
                bgcolor: isDark ? "#0b1437" : "#f8fafc",
                "& fieldset": {
                  borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                },
                "&:hover fieldset": {
                  borderColor: "#f59e0b",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#f59e0b",
                },
              },
            }}
            sx={{ width: { xs: "100%", md: 280 } }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 0.5,
              borderRadius: "12px",
              bgcolor: isDark ? "#0b1437" : "#f1f5f9",
              border: "1px solid",
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
            }}
          >
            <Tooltip title="Card Grid">
              <IconButton
                size="small"
                onClick={() => setViewMode("grid")}
                sx={{
                  borderRadius: "8px",
                  p: 0.75,
                  bgcolor:
                    viewMode === "grid"
                      ? isDark
                        ? "#111c44"
                        : "#ffffff"
                      : "transparent",
                  color: viewMode === "grid" ? "#f59e0b" : "inherit",
                  boxShadow:
                    viewMode === "grid" ? "0 2px 6px rgba(0,0,0,0.1)" : "none",
                }}
              >
                <GridViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Table View">
              <IconButton
                size="small"
                onClick={() => setViewMode("table")}
                sx={{
                  borderRadius: "8px",
                  p: 0.75,
                  bgcolor:
                    viewMode === "table"
                      ? isDark
                        ? "#111c44"
                        : "#ffffff"
                      : "transparent",
                  color: viewMode === "table" ? "#f59e0b" : "inherit",
                  boxShadow:
                    viewMode === "table" ? "0 2px 6px rgba(0,0,0,0.1)" : "none",
                }}
              >
                <ViewListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Paper>

      {/* ================= MEETINGS CONTENT ================= */}
      {loading ? (
        <Box
          sx={{
            py: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={44} sx={{ color: "#f59e0b" }} />
          <Typography sx={{ color: "text.secondary", mt: 2, fontWeight: 500 }}>
            Loading meetings...
          </Typography>
        </Box>
      ) : filteredMeetings.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            py: 10,
            px: 3,
            textAlign: "center",
            borderRadius: "20px",
            border: "1px dashed",
            borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
            bgcolor: isDark ? "rgba(17, 28, 68, 0.4)" : "rgba(255,255,255,0.6)",
          }}
        >
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: "50%",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7",
              color: "#d97706",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <VideoCallIcon sx={{ fontSize: 38 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#2b3674" }}
          >
            {search
              ? "No meetings match your search"
              : "No meetings scheduled yet"}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: 440,
              mx: "auto",
              mt: 1,
              mb: 3,
              fontSize: "0.9rem",
            }}
          >
            {search
              ? "Try adjusting your search terms or switching tabs."
              : "Anyone on your team can schedule meetings, invite colleagues, and collaborate on meeting documents and notes."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingMeeting(null);
              setIsCreateEditOpen(true);
            }}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "12px",
              px: 3,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Schedule a Meeting
          </Button>
        </Paper>
      ) : viewMode === "grid" ? (
        /* ================= CARDS GRID VIEW ================= */
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {filteredMeetings.map((m) => {
            const meetingDate = parseMeetingDate(m.meeting_datetime);
            const isPast = meetingDate ? meetingDate < new Date() : false;
            const isToday = meetingDate
              ? meetingDate.toDateString() === new Date().toDateString()
              : false;

            const statusLabel = isPast
              ? "Past"
              : isToday
              ? "Today"
              : "Upcoming";
            const statusBg = isPast
              ? isDark
                ? "#2d3748"
                : "#f1f5f9"
              : isToday
              ? "#fef3c7"
              : "#ecfdf5";
            const statusTextColor = isPast
              ? isDark
                ? "#a0aec0"
                : "#64748b"
              : isToday
              ? "#b45309"
              : "#059669";

            const hasDocument = Boolean(
              m.document_content && m.document_content.trim(),
            );

            return (
              <Paper
                key={m.id}
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  bgcolor: isDark ? "#111c44" : "#ffffff",
                  border: "1px solid",
                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                    borderColor: isDark ? "#f59e0b" : "#f59e0b",
                  },
                }}
              >
                <Box>
                  {/* CARD TOP ROW */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        bgcolor: statusBg,
                        color: statusTextColor,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 24,
                        borderRadius: "8px",
                      }}
                    />

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Add Attendees">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAddPeopleMeeting(m);
                            setIsAddPeopleOpen(true);
                          }}
                          sx={{
                            color: isDark ? "#a3aed0" : "#707eae",
                            "&:hover": {
                              color: "#3b82f6",
                              bgcolor: isDark
                                ? "rgba(59,130,246,0.15)"
                                : "#eff6ff",
                            },
                          }}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Meeting">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingMeeting(m);
                            setIsCreateEditOpen(true);
                          }}
                          sx={{
                            color: isDark ? "#a3aed0" : "#707eae",
                            "&:hover": {
                              color: "#f59e0b",
                              bgcolor: isDark
                                ? "rgba(245,158,11,0.15)"
                                : "#fef3c7",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmId(m.id)}
                          sx={{
                            color: isDark ? "#a3aed0" : "#707eae",
                            "&:hover": {
                              color: "#ef4444",
                              bgcolor: isDark
                                ? "rgba(239,68,68,0.15)"
                                : "#fee2e2",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* TITLE */}
                  <Typography
                    variant="h6"
                    onClick={() => {
                      setDetailsMeeting(m);
                      setIsDetailsOpen(true);
                    }}
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.1rem",
                      color: isDark ? "#ffffff" : "#1b2559",
                      cursor: "pointer",
                      lineHeight: 1.35,
                      mb: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      "&:hover": { color: "#f59e0b" },
                      transition: "color 0.2s ease",
                    }}
                  >
                    {m.title}
                  </Typography>

                  {/* DATE & TIME */}
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: isDark ? "#a3aed0" : "#707eae",
                        fontSize: "0.825rem",
                      }}
                    >
                      <EventIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography
                        sx={{ fontSize: "0.825rem", fontWeight: 600 }}
                      >
                        {meetingDate
                          ? meetingDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "Date TBD"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: isDark ? "#a3aed0" : "#707eae",
                        fontSize: "0.825rem",
                      }}
                    >
                      <TimeIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                      <Typography
                        sx={{ fontSize: "0.825rem", fontWeight: 600 }}
                      >
                        {meetingDate
                          ? meetingDate.toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "Time TBD"}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* HOST / CREATOR */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                      p: 1,
                      borderRadius: "10px",
                      bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
                      border: "1px solid",
                      borderColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9",
                    }}
                  >
                    <Avatar
                      src={m.creator_avatar}
                      sx={{ width: 24, height: 24, fontSize: "0.75rem", bgcolor: "#f59e0b" }}
                    >
                      {(m.creator_name || "A").charAt(0)}
                    </Avatar>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: isDark ? "#a3aed0" : "#a0aec0",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Host:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.825rem",
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1b2559",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {m.creator_name || "Admin"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ATTENDEES */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: isDark ? "#a3aed0" : "#a0aec0",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Attendees ({m.participants?.length || 0})
                    </Typography>

                    {m.participants && m.participants.length > 0 ? (
                      <AvatarGroup
                        max={5}
                        sx={{
                          justifyContent: "flex-start",
                          "& .MuiAvatar-root": {
                            width: 32,
                            height: 32,
                            fontSize: "0.75rem",
                            border: `2px solid ${
                              isDark ? "#111c44" : "#ffffff"
                            }`,
                          },
                        }}
                      >
                        {m.participants.map((p) => {
                          const name = p.employee_name || p.email;
                          return (
                            <Tooltip key={p.employee_id} title={name}>
                              <Avatar src={p.profile_pic_url} alt={name}>
                                {name.charAt(0)}
                              </Avatar>
                            </Tooltip>
                          );
                        })}
                      </AvatarGroup>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: isDark ? "#718096" : "#a0aec0",
                          fontStyle: "italic",
                        }}
                      >
                        No attendees added yet.
                      </Typography>
                    )}
                  </Box>

                  {/* DOCUMENT / MOM PILL */}
                  <Box sx={{ mb: 2.5 }}>
                    {hasDocument ? (
                      <Box
                        onClick={() => {
                          setDocumentMeeting(m);
                          setIsDocumentModalOpen(true);
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: "12px",
                          bgcolor: isDark
                            ? "rgba(139, 92, 246, 0.12)"
                            : "#faf5ff",
                          border: "1px solid",
                          borderColor: isDark
                            ? "rgba(139, 92, 246, 0.25)"
                            : "#e9d5ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: isDark
                              ? "rgba(139, 92, 246, 0.2)"
                              : "#f3e8ff",
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ overflow: "hidden" }}
                        >
                          <DocumentIcon
                            sx={{ fontSize: 18, color: "#8b5cf6" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              color: isDark ? "#c4b5fd" : "#6b21a8",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 160,
                            }}
                          >
                            {m.document_title || "Meeting Document"}
                          </Typography>
                        </Stack>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        startIcon={<DocumentIcon fontSize="small" />}
                        onClick={() => {
                          setDocumentMeeting(m);
                          setIsDocumentModalOpen(true);
                        }}
                        sx={{
                          borderRadius: "12px",
                          py: 0.75,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          textTransform: "none",
                          borderColor: isDark
                            ? "rgba(255,255,255,0.12)"
                            : "#e2e8f0",
                          color: isDark ? "#a3aed0" : "#707eae",
                          "&:hover": {
                            borderColor: "#8b5cf6",
                            color: "#8b5cf6",
                            bgcolor: isDark
                              ? "rgba(139,92,246,0.1)"
                              : "#faf5ff",
                          },
                        }}
                      >
                        + Prepare Document / MOM
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* CARD FOOTER */}
                <Box
                  sx={{
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  {m.meeting_link ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                        href={
                          m.meeting_link.startsWith("http")
                            ? m.meeting_link
                            : `https://${m.meeting_link}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          background:
                            "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                          color: "#ffffff",
                          borderRadius: "10px",
                          px: 2,
                          py: 0.6,
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          textTransform: "none",
                          boxShadow: "none",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                            boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                          },
                        }}
                      >
                        Join Call
                      </Button>

                      <Tooltip
                        title={copiedLinkId === m.id ? "Copied!" : "Copy Link"}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(m)}
                          sx={{
                            border: "1px solid",
                            borderColor: isDark
                              ? "rgba(255,255,255,0.1)"
                              : "#e2e8f0",
                            borderRadius: "10px",
                            p: 0.7,
                            color: isDark ? "#a3aed0" : "#707eae",
                          }}
                        >
                          {copiedLinkId === m.id ? (
                            <CheckIcon
                              fontSize="small"
                              sx={{ color: "#10b981" }}
                            />
                          ) : (
                            <CopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setEditingMeeting(m);
                        setIsCreateEditOpen(true);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: isDark ? "#a3aed0" : "#707eae",
                      }}
                    >
                      + Add Link
                    </Button>
                  )}

                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setDetailsMeeting(m);
                      setIsDetailsOpen(true);
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#f59e0b",
                      "&:hover": {
                        bgcolor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Details &rarr;
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        /* ================= TABLE VIEW ================= */
        <Paper
          elevation={0}
          sx={{
            borderRadius: "20px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                "& th": {
                  py: 2,
                  px: 2.5,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: isDark ? "#a3aed0" : "#a0aec0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  bgcolor: isDark ? "rgba(11, 20, 55, 0.5)" : "#f8fafc",
                  borderBottom: `1px solid ${
                    isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"
                  }`,
                  textAlign: "left",
                },
                "& td": {
                  py: 2,
                  px: 2.5,
                  fontSize: "0.875rem",
                  borderBottom: `1px solid ${
                    isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9"
                  }`,
                },
                "& tr:hover td": {
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                },
              }}
            >
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date & Time</th>
                  <th>Creator</th>
                  <th>Attendees</th>
                  <th>Document / MOM</th>
                  <th>Call Link</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMeetings.map((m) => {
                  const meetingDate = parseMeetingDate(m.meeting_datetime);
                  const hasDocument = Boolean(
                    m.document_content && m.document_content.trim(),
                  );

                  return (
                    <tr key={m.id}>
                      <td>
                        <Typography
                          onClick={() => {
                            setDetailsMeeting(m);
                            setIsDetailsOpen(true);
                          }}
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#ffffff" : "#1b2559",
                            cursor: "pointer",
                            "&:hover": { color: "#f59e0b" },
                          }}
                        >
                          {m.title}
                        </Typography>
                      </td>
                      <td>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: isDark ? "#e2e8f0" : "#2b3674",
                          }}
                        >
                          {meetingDate
                            ? meetingDate.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "Date TBD"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: isDark ? "#a3aed0" : "#707eae",
                          }}
                        >
                          {meetingDate
                            ? meetingDate.toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "Time TBD"}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={m.creator_avatar}
                            sx={{ width: 28, height: 28, fontSize: "0.75rem" }}
                          >
                            {(m.creator_name || "A").charAt(0)}
                          </Avatar>
                          <Typography
                            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                          >
                            {m.creator_name || "Admin"}
                          </Typography>
                        </Stack>
                      </td>
                      <td>
                        <AvatarGroup
                          max={4}
                          sx={{
                            justifyContent: "flex-start",
                            "& .MuiAvatar-root": {
                              width: 28,
                              height: 28,
                              fontSize: "0.75rem",
                            },
                          }}
                        >
                          {(m.participants || []).map((p) => (
                            <Tooltip
                              key={p.employee_id}
                              title={p.employee_name || p.email}
                            >
                              <Avatar src={p.profile_pic_url}>
                                {p.employee_name?.charAt(0)}
                              </Avatar>
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                      </td>
                      <td>
                        {hasDocument ? (
                          <Chip
                            icon={
                              <DocumentIcon
                                sx={{
                                  fontSize: "14px !important",
                                  color: "#8b5cf6",
                                }}
                              />
                            }
                            label={m.document_title || "View MOM"}
                            size="small"
                            onClick={() => {
                              setDocumentMeeting(m);
                              setIsDocumentModalOpen(true);
                            }}
                            sx={{
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              bgcolor: isDark
                                ? "rgba(139, 92, 246, 0.15)"
                                : "#f5f3ff",
                              color: isDark ? "#c4b5fd" : "#7c3aed",
                            }}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              setDocumentMeeting(m);
                              setIsDocumentModalOpen(true);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              color: isDark ? "#a3aed0" : "#707eae",
                            }}
                          >
                            + Prepare
                          </Button>
                        )}
                      </td>
                      <td>
                        {m.meeting_link ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                              href={
                                m.meeting_link.startsWith("http")
                                  ? m.meeting_link
                                  : `https://${m.meeting_link}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                borderRadius: "8px",
                                py: 0.4,
                                borderColor: "#f59e0b",
                                color: "#f59e0b",
                                "&:hover": {
                                  bgcolor: "rgba(245,158,11,0.08)",
                                  borderColor: "#d97706",
                                },
                              }}
                            >
                              Join
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyLink(m)}
                            >
                              <CopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Typography
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.85rem",
                            }}
                          >
                            —
                          </Typography>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Add Attendees">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setAddPeopleMeeting(m);
                                setIsAddPeopleOpen(true);
                              }}
                            >
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingMeeting(m);
                                setIsCreateEditOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirmId(m.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ================= MODALS ================= */}
      <CreateEditMeetingModal
        open={isCreateEditOpen}
        onClose={() => {
          setIsCreateEditOpen(false);
          setEditingMeeting(null);
        }}
        onSave={handleSaveMeeting}
        meeting={editingMeeting}
        employees={employees}
        loading={actionLoading}
      />

      <DocumentEditorViewerModal
        open={isDocumentModalOpen}
        onClose={() => {
          setIsDocumentModalOpen(false);
          setDocumentMeeting(null);
        }}
        meeting={documentMeeting}
        onSaveDocument={handleSaveDocument}
        loading={actionLoading}
      />

      <AddParticipantsModal
        open={isAddPeopleOpen}
        onClose={() => {
          setIsAddPeopleOpen(false);
          setAddPeopleMeeting(null);
        }}
        meeting={addPeopleMeeting}
        employees={employees}
        onAddParticipants={handleAddParticipants}
        loading={actionLoading}
      />

      <MeetingDetailsModal
        open={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setDetailsMeeting(null);
        }}
        meeting={detailsMeeting}
        onEdit={(m) => {
          setIsDetailsOpen(false);
          setEditingMeeting(m);
          setIsCreateEditOpen(true);
        }}
        onOpenDocument={(m) => {
          setDocumentMeeting(m);
          setIsDocumentModalOpen(true);
        }}
        onAddPeople={(m) => {
          setAddPeopleMeeting(m);
          setIsAddPeopleOpen(true);
        }}
        onRemoveParticipant={handleRemoveParticipant}
        onDelete={(id) => {
          setDeleteConfirmId(id);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: isDark ? "#111c44" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#1b2559" }}
        >
          Delete Meeting?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
            Are you sure you want to delete this meeting? All participant
            records and prepared meeting notes will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            color="inherit"
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMeeting}
            variant="contained"
            color="error"
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 700,
            }}
          >
            Delete Meeting
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
